import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate, Outlet } from "react-router-dom";
import "./App.css";

// --- Dashboard Components & Auth ---
import { AuthProvider, useAuth } from "./context/AuthContext";

import Login from "./pages/Login";
import LoginOTP from "./pages/LoginOTP";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import Timeliner from "./pages/Timeliner";
import VoiceDashboard from "./pages/VoiceDashboard";
import BackgroundVoiceDashboard from "./pages/BackgroundVoiceDashboard";
import CreateAccount from "./pages/CreateAccount";
import AdminDashboard from "./pages/AdminDashboard";
import DidNumbersDashboard from "./pages/DidNumbersDashboard";
import InboundRoutesDashboard from "./pages/InboundRoutesDashboard";
import CarrierTrunksDashboard from "./pages/CarrierTrunksDashboard";
import ClientGroupsDashboard from "./pages/ClientGroupsDashboard";
import ProvidersDashboard from "./pages/ProvidersDashboard";
import DeployGpuDashboard from "./pages/DeployGpuDashboard";
import MovePromptDashboard from "./pages/MovePromptDashboard";
import DeveloperApiKeysDashboard from "./pages/DeveloperApiKeysDashboard";
import InstantCloneDashboard from "./pages/InstantCloneDashboard";
import PronunciationDashboard from "./pages/PronunciationDashboard";
import PatientLibrary from "./pages/PatientLibrary";
import DemoAgentPromptsDashboard from "./pages/DemoAgentPromptsDashboard";
import SpamDetectionDashboard from "./pages/SpamDetectionDashboard";
import CurrentStatsDashboard from "./pages/CurrentStatsDashboard";
import { isClinicalSimUser } from './lib/userAccess';
import { ClinicalSimLayout } from './clinicalsim/components/ClinicalSimLayout';
import TrainingLibrary from './clinicalsim/pages/TrainingLibrary';
import CaseSession from './clinicalsim/pages/CaseSession';
import MySessions from './clinicalsim/pages/MySessions';
import Prescriptions from './clinicalsim/pages/Prescriptions';
import Scorecard from './clinicalsim/pages/Scorecard';
import { canAccessAnalytics, canAccessChatbot, canAccessCrm, canAccessInboundCalling, canAccessKnowledge, canAccessProducts, canAccessSupervision, canAccessVoiceCloning, canAccessWorkflows, canManageAgents, getDefaultAuthenticatedRoute, isAdminUser, isTimelinerUser } from "./lib/userAccess";
import { CallbacksPage } from "./features/callbacks/callbacks-page";
import { CallsPage } from "./features/calls/calls-page";
import { PhoneNumbersPage } from "./features/phone-numbers/phone-numbers-page";
import { CartPage } from "./features/cart/cart-page";
import { OrderDetailsPage } from "./features/orders/order-details-page";
import { OrderHistoryPage } from "./features/orders/order-history-page";
import { ChatbotPage } from "./features/chatbot/chatbot-page";
import { ChatbotHistory } from "./features/chatbot/chatbot-history";
import { TemplatesPage } from "./features/whatsapp-templates/templates-page";
import { TemplateEditorPage } from "./features/whatsapp-templates/template-editor-page";
import { CampaignsPage as WhatsAppCampaignsPage } from "./features/whatsapp-templates/campaigns-page";
import { WhatsAppDashboardPage } from "./features/whatsapp-dashboard/whatsapp-dashboard-page";
import { AdminProductsPage } from "./features/products/admin-products-page";
import { AdminRefundRequestsPage } from "./features/refunds/admin-refund-requests-page";
import { ProductsPage } from "./features/products/products-page";
import { AnalyticsPage } from "./features/analytics/analytics-page";
import { BillingPage } from "./features/billing/billing-page";
import { SettingsPage } from "./features/settings/settings-page";
import { AdminSettingsPage } from "./features/admin-settings/admin-settings-page";
import { OrganisationPage } from "./features/organisation/organisation-page";
import { VoicesPage } from "./features/voices/voices-page";
import { KnowledgePage } from "./features/knowledge/knowledge-page";
import { HomePage } from "./features/home/home-page";
import { LeadsPage } from "./features/crm/leads-page";
import { LeadRecordPage } from "./features/crm/lead-record-page";
import { NotesPage } from "./features/crm/notes-page";
import { CalendarPage } from "./features/crm/calendar-page";
import { FollowUpsPage } from "./features/crm/follow-ups-page";
import { CrmBatchesPage } from "./features/crm/batches-page";
import { CrmRolesPage } from "./features/crm/roles-page";
import { DndPage } from "./features/crm/dnd-page";
import { AssignmentPage } from "./features/crm/assignment-page";
import { SlaPage } from "./features/crm/sla-page";
import { AppointmentsPage } from "./features/crm/appointments-page";
import { DuplicatesPage } from "./features/crm/duplicates-page";
import { CampaignsPage } from "./features/crm/campaigns-page";
import { DialerPage } from "./features/crm/dialer-page";
import { CrmTeamPage } from "./features/crm/team-page";
import { PublicCrmFormPage } from "./features/crm/public-form-page";
import { SupervisionPage } from "./features/supervision/supervision-page";
import { RuleSettingsPage } from "./features/supervision/rules/rule-settings-page";
import { CallAnalyticsPage } from "./features/call-analytics/call-analytics-page";
import { PromptInsightsPage } from "./features/call-analytics/prompt-insights-page";
import { CallAnalyticsSettingsPage } from "./features/call-analytics/settings-page";
import { RequirePermission } from "./features/call-analytics/require-permission";
import { BatchesPage } from "./features/batches/batches-page";
import { CreateBatchWizard } from "./features/batches/wizard/create-batch-wizard";
import { AutoBuilderPage } from "./features/autobuilder/autobuilder-page";
import { AutoBuilderModePage } from "./features/autobuilder/autobuilder-mode-page";
import { SingleAgentStudio } from "./features/agents/single-agent-studio";
import { WorkflowStudio } from "./features/workflows/workflow-studio";
import { AutomationStudio } from "./features/workflows/automation-studio";
import { AutomationListPage } from "./features/workflows/automation-list-page";
import { AutomationRunsPage } from "./features/workflows/automation-runs-page";
import { AutomationSchedulePage } from "./features/workflows/automation-schedule-page";
import { AutomationContactsPage } from "./features/workflows/automation-contacts-page";
import { AutomationContactEditorPage } from "./features/workflows/automation-contact-editor-page";
import { TokenSheet } from "./features/theme/token-sheet";
import { SfaDashboardPage } from "./features/crm/sfa-dashboard-page";
import { AcceptInvitationPage } from "./features/invitations/accept-invitation-page";
import { INVITE_ACCEPT_PATH, safeReturnPath } from "./features/invitations/accept-token";


// --- Helper Components ---
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.toggle("crm-route", pathname.startsWith("/crm") || pathname.startsWith("/forms/crm"));
    return () => document.body.classList.remove("crm-route");
  }, [pathname]);
  return null;
}

function LegacyAutoBuilderRedirect() {
  const { sessionId = "new" } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/home/autobuilder/single/${encodeURIComponent(sessionId)}${search}`} replace />;
}

// Protected Route Component for Dashboard
interface ProtectedRouteProps {
  children?: React.ReactNode;
  allowWhen?: ((user: any) => boolean) | null;
  accessCheck?: ((user: any) => boolean) | null;
}

const ProtectedRoute = ({ children, allowWhen = null, accessCheck = null }: ProtectedRouteProps) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-paper-page dark:bg-[#1C1917]">
        <div className="w-8 h-8 border-4 border-accent-500 rounded-full border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role === 'viewer' && accessCheck !== isDashboardAccessUser) {
    return <Navigate to="/dashboard-access/users" replace />;
  }
  
  // Strict isolation: ClinicalSim users cannot access standard platform routes
  if (isClinicalSimUser(user) && accessCheck !== isClinicalSimUser) {
    return <Navigate to="/cs/library" replace />;
  }
  
  const check = accessCheck || allowWhen;
  if (typeof check === 'function' && !check(user)) {
    return <Navigate to={getDefaultAuthenticatedRoute(user)} replace />;
  }
  return children ? <>{children}</> : <Outlet />;
};

// Public Route Component for Auth Pages
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const { search } = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-paper-page dark:bg-[#1C1917]">
        <div className="w-8 h-8 border-4 border-accent-500 rounded-full border-t-transparent animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Signing in from an invite link returns to it; anything else goes where it always did.
    const next = safeReturnPath(new URLSearchParams(search).get("next"));
    return <Navigate to={next ?? getDefaultAuthenticatedRoute(user)} replace />;
  }
  return <>{children}</>;
};


import { AppLayout } from './components/layout/AppLayout';
import { useNavStore } from './components/layout/nav-store';
import { Menu } from 'lucide-react';
import { DashboardAccessBanner } from './components/layout/DashboardAccessBanner';
import { VpnAlertBanner } from './components/VpnAlertBanner';
import DashboardAccessUsers from './pages/DashboardAccessUsers';
const isDashboardAccessUser = (user: any) => user?.role === 'viewer';
// Full-height chat page: own mobile nav button since the global TopBar is hidden here.
function WhatsAppConversationsPage() {
  const openMobileNav = useNavStore((s) => s.openMobileNav);
  return (
    <div className="flex h-full flex-col gap-3 px-4 sm:px-5 md:px-6 pb-3 pt-3">
      <div className="flex shrink-0 items-center gap-2 md:hidden">
        <button
          type="button"
          onClick={openMobileNav}
          aria-label="Open navigation menu"
          className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-3 transition-colors hover:bg-soft hover:text-ink"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>
      <ChatbotHistory />
    </div>
  );
}
// --- Main App Component ---
function AppContent() {
  return (
    <Routes>
      <Route path="/forms/crm/:token" element={<PublicCrmFormPage />} />
      {/* --- Auth Routes (No Header/Footer) --- */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/login/phone" element={<PublicRoute><LoginOTP /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      {/* Neither public nor protected: it explains sign-in itself, then accepts once signed in. */}
      <Route path={INVITE_ACCEPT_PATH} element={<AcceptInvitationPage />} />
      <Route path="/dashboard-access/users" element={<ProtectedRoute accessCheck={isDashboardAccessUser}><DashboardAccessUsers /></ProtectedRoute>} />

      {/* --- Protected Dashboard Routes (No Header/Footer) --- */}
      {/* Redirect old /dashboard path to /analytics to avoid 404s for existing bookmarks */}
      <Route path="/dashboard" element={<ProtectedRoute allowWhen={canAccessAnalytics}><Navigate to="/analytics" replace /></ProtectedRoute>} />
      
      {/* Fullscreen Editors */}
      <Route path="/home/create-automation" element={<ProtectedRoute allowWhen={canAccessWorkflows}><AutomationStudio /></ProtectedRoute>} />

      <Route element={<AppLayout />}>
        <Route path="/analytics" element={<ProtectedRoute allowWhen={canAccessAnalytics}><AnalyticsPage /></ProtectedRoute>} />
        <Route
          path="/timeliner"
          element={
            <ProtectedRoute allowWhen={(user) => isAdminUser(user) || isTimelinerUser(user)}>
              <Timeliner />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<ProtectedRoute allowWhen={(user) => !isAdminUser(user)}><HomePage /></ProtectedRoute>} />
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/crm" element={<ProtectedRoute allowWhen={canAccessCrm}><SfaDashboardPage /></ProtectedRoute>} />
        <Route path="/crm/follow-ups" element={<ProtectedRoute allowWhen={canAccessCrm}><FollowUpsPage /></ProtectedRoute>} />
        <Route path="/crm/batches" element={<ProtectedRoute allowWhen={canAccessCrm}><CrmBatchesPage /></ProtectedRoute>} />
        <Route path="/crm/batches/:batchId" element={<ProtectedRoute allowWhen={canAccessCrm}><AutomationContactEditorPage backHref="/crm/batches" backLabel="Batches" /></ProtectedRoute>} />
        <Route path="/crm/agents" element={<ProtectedRoute allowWhen={canAccessCrm}><CrmTeamPage agents /></ProtectedRoute>} />
        <Route path="/crm/users" element={<ProtectedRoute allowWhen={canAccessCrm}><CrmTeamPage /></ProtectedRoute>} />
        <Route path="/crm/roles" element={<ProtectedRoute allowWhen={canAccessCrm}><CrmRolesPage /></ProtectedRoute>} />
        <Route path="/crm/suppression" element={<ProtectedRoute allowWhen={canAccessCrm}><DndPage /></ProtectedRoute>} />
        <Route path="/crm/assignment" element={<ProtectedRoute allowWhen={canAccessCrm}><AssignmentPage /></ProtectedRoute>} />
<Route path="/crm/sla" element={<ProtectedRoute allowWhen={canAccessCrm}><SlaPage /></ProtectedRoute>} />
        <Route path="/crm/appointments" element={<ProtectedRoute allowWhen={canAccessCrm}><AppointmentsPage /></ProtectedRoute>} />
        <Route path="/crm/notes" element={<ProtectedRoute allowWhen={canAccessCrm}><NotesPage /></ProtectedRoute>} />
        <Route path="/crm/calendar" element={<ProtectedRoute allowWhen={canAccessCrm}><CalendarPage /></ProtectedRoute>} />
        <Route path="/crm/duplicates" element={<ProtectedRoute allowWhen={canAccessCrm}><DuplicatesPage /></ProtectedRoute>} />
        <Route path="/crm/notifications" element={<ProtectedRoute allowWhen={canAccessCrm}><Navigate to="/crm" replace /></ProtectedRoute>} />
        <Route path="/crm/campaigns" element={<ProtectedRoute allowWhen={canAccessCrm}><CampaignsPage /></ProtectedRoute>} />
        <Route path="/crm/dialer" element={<ProtectedRoute allowWhen={canAccessCrm}><DialerPage /></ProtectedRoute>} />
        <Route path="/crm/leads" element={<ProtectedRoute allowWhen={canAccessCrm}><LeadsPage key="leads" /></ProtectedRoute>} />
        <Route path="/crm/leads/:leadId" element={<ProtectedRoute allowWhen={canAccessCrm}><LeadRecordPage /></ProtectedRoute>} />
        <Route path="/crm/customers" element={<ProtectedRoute allowWhen={canAccessCrm}><LeadsPage key="customers" customers /></ProtectedRoute>} />
        <Route path="/crm/customers/:leadId" element={<ProtectedRoute allowWhen={canAccessCrm}><LeadRecordPage customers /></ProtectedRoute>} />
        <Route path="/batch" element={<ProtectedRoute><BatchesPage /></ProtectedRoute>} />
        <Route path="/batch/new" element={<ProtectedRoute><CreateBatchWizard /></ProtectedRoute>} />
        <Route path="/batch/callbacks" element={<ProtectedRoute><CallbacksPage /></ProtectedRoute>} />

        <Route path="/home/create-agent" element={<ProtectedRoute allowWhen={canManageAgents}><SingleAgentStudio /></ProtectedRoute>} />
        <Route path="/home/create-workflow" element={<ProtectedRoute allowWhen={canManageAgents}><WorkflowStudio /></ProtectedRoute>} />
        <Route path="/home/automation" element={<ProtectedRoute allowWhen={canAccessWorkflows}><AutomationListPage /></ProtectedRoute>} />
        <Route path="/home/automation/runs" element={<ProtectedRoute allowWhen={canAccessWorkflows}><AutomationRunsPage /></ProtectedRoute>} />
        <Route path="/home/automation/schedule" element={<ProtectedRoute allowWhen={canAccessWorkflows}><AutomationSchedulePage /></ProtectedRoute>} />
        <Route path="/home/automation/contacts" element={<ProtectedRoute allowWhen={canAccessWorkflows}><AutomationContactsPage /></ProtectedRoute>} />
        <Route path="/home/automation/contacts/:batchId" element={<ProtectedRoute allowWhen={canAccessWorkflows}><AutomationContactEditorPage /></ProtectedRoute>} />
        <Route path="/home/autobuilder" element={<ProtectedRoute allowWhen={canManageAgents}><AutoBuilderModePage /></ProtectedRoute>} />
        <Route path="/home/autobuilder/single/:sessionId" element={<ProtectedRoute allowWhen={canManageAgents}><AutoBuilderPage /></ProtectedRoute>} />
        <Route path="/home/autobuilder/:sessionId" element={<ProtectedRoute allowWhen={canManageAgents}><LegacyAutoBuilderRedirect /></ProtectedRoute>} />
        {/* /schedule-call retired — replaced by Batch Workspace (/batch/new) */}
        <Route path="/phone-numbers" element={<ProtectedRoute allowWhen={canAccessInboundCalling}><PhoneNumbersPage /></ProtectedRoute>} />
        <Route path="/call-history" element={<ProtectedRoute><CallsPage /></ProtectedRoute>} />
        <Route path="/chatbot" element={<ProtectedRoute allowWhen={canAccessChatbot}><ChatbotPage /></ProtectedRoute>} />
        <Route path="/whatsapp" element={<Navigate to="/whatsapp/overview" replace />} />
        <Route path="/whatsapp/agents" element={<Navigate to="/chatbot" replace />} />
        <Route path="/whatsapp/overview" element={<ProtectedRoute allowWhen={canAccessChatbot}><WhatsAppDashboardPage /></ProtectedRoute>} />
        <Route path="/whatsapp/dashboard" element={<Navigate to="/whatsapp/overview" replace />} />
        <Route path="/whatsapp/conversations" element={<ProtectedRoute allowWhen={canAccessChatbot}><WhatsAppConversationsPage /></ProtectedRoute>} />
        <Route path="/whatsapp/contacts" element={<ProtectedRoute allowWhen={(user) => canAccessChatbot(user) || canAccessWorkflows(user)}><AutomationContactsPage title="Import Contact" uploadSource="whatsapp" sourceFilter="whatsapp" detailBasePath="/whatsapp/contacts" /></ProtectedRoute>} />
        <Route path="/whatsapp/contacts/:batchId" element={<ProtectedRoute allowWhen={(user) => canAccessChatbot(user) || canAccessWorkflows(user)}><AutomationContactEditorPage backHref="/whatsapp/contacts" backLabel="Import Contact" /></ProtectedRoute>} />
        <Route path="/whatsapp/templates" element={<ProtectedRoute allowWhen={(user) => canAccessChatbot(user) || canAccessWorkflows(user)}><TemplatesPage /></ProtectedRoute>} />
        <Route path="/whatsapp/templates/new" element={<ProtectedRoute allowWhen={(user) => canAccessChatbot(user) || canAccessWorkflows(user)}><TemplateEditorPage /></ProtectedRoute>} />
        <Route path="/whatsapp/templates/:templateId" element={<ProtectedRoute allowWhen={(user) => canAccessChatbot(user) || canAccessWorkflows(user)}><TemplateEditorPage /></ProtectedRoute>} />
        <Route path="/whatsapp/campaigns" element={<ProtectedRoute allowWhen={(user) => canAccessChatbot(user) || canAccessWorkflows(user)}><WhatsAppCampaignsPage /></ProtectedRoute>} />
        <Route path="/supervision" element={<ProtectedRoute allowWhen={canAccessSupervision}><SupervisionPage /></ProtectedRoute>} />
        <Route path="/supervision/flagged" element={<ProtectedRoute allowWhen={canAccessSupervision}><SupervisionPage /></ProtectedRoute>} />
        <Route path="/supervision/insights" element={<ProtectedRoute allowWhen={canAccessSupervision}><SupervisionPage /></ProtectedRoute>} />
        <Route path="/supervision/rules" element={<ProtectedRoute allowWhen={canAccessSupervision}><RuleSettingsPage /></ProtectedRoute>} />
        <Route path="/call-analytics" element={<ProtectedRoute><RequirePermission permission="call_analytics.view"><CallAnalyticsPage /></RequirePermission></ProtectedRoute>} />
        <Route path="/call-analytics/improvements" element={<ProtectedRoute><RequirePermission permission="call_analytics.view"><PromptInsightsPage /></RequirePermission></ProtectedRoute>} />
        <Route path="/call-analytics/settings" element={<ProtectedRoute><RequirePermission permission="call_analytics.view"><CallAnalyticsSettingsPage /></RequirePermission></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute allowWhen={(user) => user?.audit_and_billing !== false}><BillingPage /></ProtectedRoute>} />
        <Route path="/voices" element={<ProtectedRoute allowWhen={canAccessVoiceCloning}><VoicesPage /></ProtectedRoute>} />
        <Route path="/knowledge" element={<ProtectedRoute allowWhen={canAccessKnowledge}><KnowledgePage /></ProtectedRoute>} />
        <Route path="/organisation" element={<ProtectedRoute><OrganisationPage /></ProtectedRoute>} />
        <Route path="/products" element={<ProtectedRoute allowWhen={canAccessProducts}><ProductsPage /></ProtectedRoute>} />
        <Route path="/cart" element={<ProtectedRoute allowWhen={canAccessProducts}><CartPage /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute allowWhen={canAccessProducts}><OrderHistoryPage /></ProtectedRoute>} />
        <Route path="/orders/:orderId" element={<ProtectedRoute allowWhen={canAccessProducts}><OrderDetailsPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/theme/tokens" element={<ProtectedRoute><TokenSheet /></ProtectedRoute>} />

        <Route path="/admin/users" element={<ProtectedRoute allowWhen={isAdminUser}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/voice" element={<ProtectedRoute allowWhen={isAdminUser}><VoiceDashboard /></ProtectedRoute>} />
        <Route path="/admin/background-audio" element={<ProtectedRoute allowWhen={isAdminUser}><BackgroundVoiceDashboard /></ProtectedRoute>} />
        <Route path="/admin/accounts" element={<ProtectedRoute allowWhen={isAdminUser}><CreateAccount /></ProtectedRoute>} />
        <Route path="/admin/did-numbers" element={<ProtectedRoute allowWhen={isAdminUser}><DidNumbersDashboard /></ProtectedRoute>} />
        <Route path="/admin/inbound-routes" element={<ProtectedRoute allowWhen={isAdminUser}><InboundRoutesDashboard /></ProtectedRoute>} />
        <Route path="/admin/carrier-trunks" element={<ProtectedRoute allowWhen={isAdminUser}><CarrierTrunksDashboard /></ProtectedRoute>} />
        <Route path="/admin/client-groups" element={<ProtectedRoute allowWhen={isAdminUser}><ClientGroupsDashboard /></ProtectedRoute>} />
        <Route path="/admin/products" element={<ProtectedRoute allowWhen={isAdminUser}><AdminProductsPage /></ProtectedRoute>} />
        <Route path="/admin/refund-requests" element={<ProtectedRoute allowWhen={isAdminUser}><AdminRefundRequestsPage /></ProtectedRoute>} />
        <Route path="/admin/providers" element={<ProtectedRoute allowWhen={isAdminUser}><ProvidersDashboard /></ProtectedRoute>} />
        <Route path="/admin/deploy-gpu" element={<ProtectedRoute allowWhen={isAdminUser}><DeployGpuDashboard /></ProtectedRoute>} />
        <Route path="/admin/prompts" element={<ProtectedRoute allowWhen={isAdminUser}><MovePromptDashboard /></ProtectedRoute>} />
        <Route path="/admin/instant-clone" element={<ProtectedRoute allowWhen={isAdminUser}><InstantCloneDashboard /></ProtectedRoute>} />
        <Route path="/admin/pronunciations" element={<ProtectedRoute allowWhen={isAdminUser}><PronunciationDashboard /></ProtectedRoute>} />
        <Route path="/admin/patient-library" element={<ProtectedRoute allowWhen={isAdminUser}><PatientLibrary /></ProtectedRoute>} />
        <Route path="/admin/demo-prompts" element={<ProtectedRoute allowWhen={isAdminUser}><DemoAgentPromptsDashboard /></ProtectedRoute>} />
        <Route path="/admin/spam-detection" element={<ProtectedRoute allowWhen={isAdminUser}><SpamDetectionDashboard /></ProtectedRoute>} />
        <Route path="/admin/current-stats" element={<ProtectedRoute allowWhen={isAdminUser}><CurrentStatsDashboard /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute allowWhen={isAdminUser}><AdminSettingsPage /></ProtectedRoute>} />
      </Route>
      <Route path="/developer-api-keys" element={<ProtectedRoute><DeveloperApiKeysDashboard /></ProtectedRoute>} />

      <Route path="/cs" element={<ProtectedRoute accessCheck={isClinicalSimUser} />}>
        <Route element={<ClinicalSimLayout />}>
          <Route path="library" element={<TrainingLibrary />} />
          <Route path="sessions" element={<MySessions />} />
          <Route path="sessions/:sessionId" element={<CaseSession />} />
          <Route path="prescriptions" element={<Prescriptions />} />
          <Route path="scorecard" element={<Scorecard />} />
        </Route>
      </Route>

      {/* Catch all - redirect unknown paths back to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

import { CustomToaster } from "./components/CustomToast";
import CreditAlertBanner from "./components_dashboard/CreditAlertBanner";
import { CreditAlertProvider } from "./context/CreditAlertContext";
import { LiveCallProvider } from "./context/LiveCallContext";
import LiveCallOverlay from "./components_dashboard/LiveCallOverlay";
import { ToastProvider } from "./components/ui/toast";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>
            {/* AuthProvider wraps everything so context is available everywhere */}
            <AuthProvider>
              <LiveCallProvider>
                <CreditAlertProvider>
                  <ScrollToTop />
                  <DashboardAccessBanner />
                  <VpnAlertBanner />
                  <AppContent />
                  <CreditAlertBanner />
                  <LiveCallOverlay />
                  <CustomToaster />
                </CreditAlertProvider>
              </LiveCallProvider>
            </AuthProvider>
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </div>
  );
}

export default App;
